import styled from 'styled-components';
import Calendar from 'react-calendar';
import { format } from 'date-fns';
import { Button, Icon } from '@successmode-ktp/kui';
import { DEPARTURE } from '@repo/constants';
import { useEffect, useRef, useState } from 'react';
import { ICON_CLOSE } from '@repo/assets';

type Props = {
  departureDate?: string;
  nationality?: string;
  firstRefundDate?: string;
  onChange?: (v?: any) => void;
  onClose: (v?: any) => void;
  onClick: (v?: any) => void;
};
export const CalendarModal = ({
  onChange,
  departureDate,
  nationality = 'ENG',
  firstRefundDate,
  onClose,
  onClick,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const LANG = nationality === 'KOR' ? 'KOR' : 'ENG';
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        buttonRef.current.contains(event.target as Node)
      ) {
        return;
      }
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // 모달 바깥 클릭 시 닫기
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <BackDrop>
      <BodyWrapper>
        <TabBar />
        <HeaderWrapper>
          <Title>
            {DEPARTURE[LANG].title}
            <button
              type='button'
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
            >
              <Icon.QuestionFilled fillColor='#9FA1A7' />
            </button>
          </Title>
          <button type='button' onClick={onClose}>
            <img src={ICON_CLOSE} alt='close' width={20} height={20} />
          </button>
        </HeaderWrapper>
        <CalendarWrapper>
          <Calendar
            onChange={onChange}
            value={departureDate}
            formatDay={(_locale, date) => format(date, 'dd')}
            showNeighboringMonth={false}
            maxDetail='month'
            minDetail='month'
            next2Label={null}
            prev2Label={null}
            calendarType='gregory'
            locale={nationality === 'KOR' ? 'ko-KR' : 'en-US'}
            minDate={new Date(firstRefundDate || '')}
            className='notranslate'
          />
        </CalendarWrapper>
        {isOpen && (
          <ExplainWrapper dir='column' ref={modalRef}>
            <ExplainTitle>{DEPARTURE[LANG].tooltipTitle}</ExplainTitle>
            <div>
              <SubTitle>{DEPARTURE[LANG].firstTitle}</SubTitle>
              <Desc>{DEPARTURE[LANG].firstContents}</Desc>
            </div>
            <div>
              <SubTitle>{DEPARTURE[LANG].secondTitle}</SubTitle>
              <Desc>{DEPARTURE[LANG].secondContents}</Desc>
            </div>
          </ExplainWrapper>
        )}
        <ButtonWrapper>
          <Button type='button' onClick={onClick}>
            {DEPARTURE[LANG].btnOk}
          </Button>
        </ButtonWrapper>
      </BodyWrapper>
    </BackDrop>
  );
};

const BackDrop = styled.div`
  display: flex;
  align-items: flex-end;
  position: fixed;
  inset: 0 0 0 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
`;

const TabBar = styled.div`
  width: 44px;
  height: 4px;
  border-radius: 4px;
  background: var(--Foundation-Grey-G03, #dedede);
  margin: auto;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eeeeee;
`;

const Title = styled.h6`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 20px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.4px;
`;

const BodyWrapper = styled.div`
  position: relative;
  margin: 0 auto;
  background-color: #ffffff;
  padding: 8px 0 20px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  max-width: 500px;
`;

const ButtonWrapper = styled.div`
  padding: 0 20px;
  margin-top: 20px;
`;

const ExplainWrapper = styled.div`
  position: absolute;
  top: 65px;
  right: 30px;
  width: 270px;
  padding: 16px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(25, 25, 25, 0.18);
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const ExplainTitle = styled.strong`
  display: block;
  color: var(--KTP-Grayscale-Gray_03, #333);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const SubTitle = styled.p`
  color: var(--KTP-Grayscale-Gray_03, #333);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px; /* 166.667% */
`;

const Desc = styled.p`
  color: var(--KTP-Grayscale-80, var(--KTP-Color-Mono-80, #3a3b3e));
  font-size: 12px;
  font-weight: 400;
  line-height: 140%; /* 16.8px */
  letter-spacing: -0.24px;
`;

const CalendarWrapper = styled.div`
  .react-calendar {
    border: none;
    padding: 0 20px;
    width: 100%;
  }
  .react-calendar__navigation__label__labelText {
    font-size: 18px;
    font-weight: 600;
  }
  .react-calendar__navigation__arrow {
    font-size: 28px;
    font-weight: 300;
  }
  .react-calendar__navigation button:disabled {
    background: #ffffff;
    color: black;
  }
  .react-calendar__tile {
    aspect-ratio: 1 !important;
  }
  .react-calendar__tile:disabled {
    color: gray;
    cursor: not-allowed;
  }
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus,
  .react-calendar__tile--now:enabled:hover,
  .react-calendar__tile--now:enabled:focus {
    background: #1087ff;
  }
  .react-calendar__tile--now {
    background: #fff;
  }
  .react-calendar__tile--now.react-calendar__tile--active {
    background: #006edc;
  }
  .react-calendar__tile--now::after {
    content: '•';
    font-size: 16px;
    width: 0;
    display: inline-block;
    color: blue;
    transform: translate(-11px, -13px);
  }
  .react-calendar__tile--active {
    border-radius: 50%;
  }

  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }
`;
